import { useAnalytics } from "@codat/analytics";
import {
  BinIcon,
  PaginationRange,
  PencilIcon,
  useToast,
} from "@codat/orchard-ui";
import { ColumnDefinition } from "@codat/orchard-ui/dist/es/components/DataTable/types";
import { AlertingRule, GetCompaniesResponse } from "api/public";
import { PagedResponse } from "api/shared";
import classNames from "classnames";
import React, { useCallback, useMemo } from "react";

import Button from "components/Button";
import DataTable from "components/DataTable";
import GenericCell from "components/GenericCell";

import { formatRuleName } from "./utils";
import styles from "./WebhookRulesTable.module.css";

type CtaCellProps = {
  key: "cta";
  value: {
    companyId: string;
    onEdit: () => void;
    onDelete: () => void;
    hoveredRowIndex: number | undefined;
    index: number;
  };
};

interface WebhookRulesTableProps {
  rules: PagedResponse<AlertingRule> | undefined;
  onEditRule?: (rule: AlertingRule) => void;
  onDeleteRule?: (rule: AlertingRule) => void;
  loading: boolean;
  pageSize: number;
  pageIndex: number;
  companies: GetCompaniesResponse | undefined;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
}

const WebhookRulesTable = ({
  rules,
  companies,
  onDeleteRule,
  onEditRule,
  loading,
  pageSize,
  setPageIndex,
}: WebhookRulesTableProps) => {
  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();

  const getCompanyName = useCallback(
    (companyId: string) => {
      if (!companies?.results) {
        return "-";
      }

      const company = companies.results.find((c) => c.id === companyId);
      if (company) {
        return company.name;
      } else {
        return "All companies";
      }
    },
    [companies]
  );

  const rowInfo = useMemo(() => {
    if (!rules?.results) return [];

    return rules.results.map((row, index) => {
      const onCopyCompanyId = () => {
        addToast({
          message: "Copied company ID to clipboard",
          variant: "success",
        });
        sendEvent("Company ID - Copy", { companyId: row.companyId });
      };

      const onCopyRuleId = () => {
        addToast({
          message: "Copied rule ID to clipboard",
          variant: "success",
        });
        sendEvent("Rule ID - Copy", { ruleId: row.id });
      };

      return {
        data: [
          {
            key: "rule",
            value: {
              content: formatRuleName(row.type),
              subtext: row.id,
              onCopySubtext: onCopyRuleId,
              clipboardCopyLabel: "Copy rule ID",
              testId: "webhook-rules-table-rule",
              index,
            },
          },
          {
            key: "company",
            value: {
              content: getCompanyName(row.companyId || ""),
              subtext: row.companyId,
              onCopySubtext: onCopyCompanyId,
              clipboardCopyLabel: "Copy company ID",
              testId: "webhook-rules-table-company",
              index,
            },
          },
          {
            key: "cta",
            value: {
              companyId: row.companyId,
              index,
              onEdit: onEditRule ? () => onEditRule(row) : undefined,
              onDelete: onDeleteRule ? () => onDeleteRule(row) : undefined,
            },
          },
        ],
      };
    });
  }, [rules, addToast, getCompanyName, onDeleteRule, onEditRule, sendEvent]);

  const CtaCell: React.FC<Pick<CtaCellProps, "value">> = ({ value }) => (
    <div className={classNames(styles.ctaButtons, styles.hover)}>
      {onEditRule && (
        <Button
          label="Edit rule"
          size="small"
          variant="secondary"
          color="interface"
          buttonName="Edit Rule"
          eventProperties={{ companyId: value.companyId }}
          data-testid="webhook-rules-table-edit"
          onClick={value.onEdit}
          className={styles.editButton}
        >
          <PencilIcon />
        </Button>
      )}
      {onDeleteRule && (
        <Button
          label="Delete rule"
          buttonName="Delete Rule"
          className={styles.deleteButton}
          variant="secondary"
          color="negative"
          onClick={() => {
            value.onDelete && value.onDelete();
            sendEvent("Delete Button - Click");
          }}
          data-testid="webhook-rules-table-delete"
        >
          <BinIcon />
        </Button>
      )}
    </div>
  );

  const columnDefs: ColumnDefinition<"rule" | "company" | "cta">[] = [
    {
      key: "rule",
      label: "Rule",
      component: GenericCell,
      colWidth: 40,
    },
    {
      key: "company",
      label: "Company",
      component: GenericCell,
    },
    {
      key: "cta",
      label: "",
      component: CtaCell,
      align: "right",
    },
  ];

  const onUpdatePageIndex = (page: number) => {
    setPageIndex(page);
    sendEvent("Webhook Rules - Pagination - Page Index - Change", {
      newPageIndex: page,
    });
  };

  const numberOfPages = rules ? Math.ceil(rules?.totalResults / pageSize) : 0;
  const columns = loading ? [] : columnDefs;

  return (
    <>
      <DataTable
        isSkeleton={loading}
        skeletonRows={pageSize}
        columnDefs={columns}
        data-testid="webhook-rules-table"
        rowsInfo={rowInfo}
        rowName="Rule"
        isLayoutAuto
        hasBorder
        className={styles.dataTable}
      />
      <PaginationRange
        total={numberOfPages}
        onUpdatePageIndex={onUpdatePageIndex}
        isPrevNext
      />
    </>
  );
};

export default WebhookRulesTable;
